// src/components/AiChat.js

import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { MessageCircle, X, Send, Bot, User, Loader } from 'lucide-react';
import './AiChat.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function AiChat() {
  const { token, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hi ${user?.first_name || 'there'}! I'm your DifferentialPay AI assistant. Ask me anything about your payments, suppliers, or financial activity. Try something like:\n\n• "Which supplier are we spending the most on?"\n• "Are there any unusual payments?"\n• "Summarize this week's activity"`,
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    const userMessage = { role: 'user', content: trimmed };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Build history (exclude the welcome message)
      const history = messages
        .filter((_, i) => i > 0) // skip welcome message
        .map(m => ({ role: m.role, content: m.content }));

      const response = await fetch(`${API_URL}/api/ai/chat/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: trimmed,
          history: history,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Sorry, I ran into an issue processing that request. Please try again.',
          isError: true 
        }]);
      }
    } catch (err) {
      console.error('AI Chat error:', err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Unable to connect to the AI service. Please check your connection.',
        isError: true 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Simple markdown-like formatting
  const formatMessage = (text) => {
    return text
      .split('\n')
      .map((line, i) => {
        // Bold
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        // Headers
        if (line.startsWith('## ')) {
          return `<h4 key=${i}>${line.slice(3)}</h4>`;
        }
        // Bullet points
        if (line.startsWith('- ') || line.startsWith('• ')) {
          return `<div class="chat-bullet">${line}</div>`;
        }
        // Numbered items
        if (/^\d+\.\s/.test(line)) {
          return `<div class="chat-bullet">${line}</div>`;
        }
        // Empty line = spacing
        if (line.trim() === '') {
          return '<div class="chat-spacer"></div>';
        }
        return `<div>${line}</div>`;
      })
      .join('');
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button className="ai-chat-fab" onClick={() => setIsOpen(true)}>
          <Bot size={24} />
          <span className="fab-label">AI Assistant</span>
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className="ai-chat-panel">
          {/* Header */}
          <div className="ai-chat-header">
            <div className="ai-chat-header-info">
              <Bot size={20} />
              <div>
                <div className="ai-chat-title">DifferentialPay AI</div>
                <div className="ai-chat-status">Powered by Differential Technologies</div>
              </div>
            </div>
            <button className="ai-chat-close" onClick={() => setIsOpen(false)}>
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="ai-chat-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`chat-message ${msg.role}`}>
                <div className="chat-message-icon">
                  {msg.role === 'assistant' ? <Bot size={16} /> : <User size={16} />}
                </div>
                <div className={`chat-message-bubble ${msg.isError ? 'error' : ''}`}>
                  <div 
                    className="chat-message-content"
                    dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} 
                  />
                </div>
              </div>
            ))}
            {loading && (
              <div className="chat-message assistant">
                <div className="chat-message-icon">
                  <Bot size={16} />
                </div>
                <div className="chat-message-bubble">
                  <div className="chat-typing">
                    <Loader size={16} className="typing-spinner" />
                    <span>Analysing your data...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="ai-chat-input-area">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your payments..."
              rows={1}
              disabled={loading}
            />
            <button 
              className="ai-chat-send" 
              onClick={sendMessage}
              disabled={!input.trim() || loading}
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default AiChat;